import React, { useMemo, useState } from 'react';
import {
  ISSUE_REPORT_NOTE_MAX_LENGTH,
  ISSUE_REPORT_REASONS,
} from '../../../constants/games/gameIssueReports';
import { INITIAL_ZOOM as GENERATIONS_INITIAL_ZOOM } from '../../../constants/games/generationsGame';
import { submitGameIssueReport } from '../../../utils/gameIssueReportApi';
import DefinitionSection from '../common/DefinitionSection';
import AnsweredGridPreview from '../shared/AnsweredGridPreview';
import ZoomedImagePreview from '../shared/ZoomedImagePreview';
import ShareZoomedImageButton from '../shared/ShareZoomedImageButton';
import RewardSummary from '../shared/RewardSummary';

const GenerationsGameModal = ({
  title,
  isWin,
  solution,
  imageUrl,
  reportRoundId = null,
  rewardCoins = null,
  pointsAwarded = 0,
  firstGameBonus = false,
  zoomLevelForShare = null,
  zoomOriginForShare = null,
  guesses = [],
  stats = null,
  user = null,
  onNextRound,
  countdown,
  disableNext = false,
  nextLabel = 'Next Round',
  showDefinition = false,
  definition = null,
  isLoadingDefinition = false,
  toggleDefinition = () => {},
}) => {
  const [showReportForm, setShowReportForm] = useState(false);
  const [selectedReason, setSelectedReason] = useState(
    ISSUE_REPORT_REASONS[0]?.value || 'image_word_mismatch'
  );
  const [impactedResult, setImpactedResult] = useState(false);
  const [note, setNote] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [reportError, setReportError] = useState('');

  const visibleRewardCoins = isWin ? rewardCoins : null;
  const previewZoomLevel = isWin ? 100 : (zoomLevelForShare ?? 100);
  const previewZoomOrigin = isWin ? null : (zoomOriginForShare ?? null);
  const copyZoomLevel = GENERATIONS_INITIAL_ZOOM;
  const copyZoomOrigin = zoomOriginForShare ?? null;
  const remainingNoteChars = useMemo(
    () => ISSUE_REPORT_NOTE_MAX_LENGTH - note.length,
    [note]
  );
  const canReport = Boolean(solution);
  const safeStats = useMemo(
    () => ({
      guesses: {
        1: Math.max(0, Number(stats?.guesses?.[1]) || 0),
        2: Math.max(0, Number(stats?.guesses?.[2]) || 0),
        3: Math.max(0, Number(stats?.guesses?.[3]) || 0),
        4: Math.max(0, Number(stats?.guesses?.[4]) || 0),
        5: Math.max(0, Number(stats?.guesses?.[5]) || 0),
      },
      losses: Math.max(0, Number(stats?.losses) || 0),
      currentStreak: Math.max(0, Number(stats?.currentStreak) || 0),
      maxStreak: Math.max(0, Number(stats?.maxStreak) || 0),
    }),
    [stats]
  );
  const wins = Object.values(safeStats.guesses).reduce((a, b) => a + b, 0);
  const totalGames = wins + safeStats.losses;
  const winPercentage = totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0;
  const maxGuesses = Math.max(...Object.values(safeStats.guesses));

  const resetReportState = () => {
    setSelectedReason(ISSUE_REPORT_REASONS[0]?.value || 'image_word_mismatch');
    setImpactedResult(false);
    setNote('');
    setReportError('');
  };

  const onToggleReportForm = () => {
    if (reportSubmitting) return;
    const next = !showReportForm;
    setShowReportForm(next);
    if (!next && !reportSuccess) {
      resetReportState();
    }
  };

  const onSubmitReport = async (event) => {
    event.preventDefault();
    if (reportSubmitting || reportSuccess || !canReport) return;

    setReportSubmitting(true);
    setReportError('');

    try {
      await submitGameIssueReport({
        gameType: 'generations',
        roundId: reportRoundId ?? null,
        reason: selectedReason,
        impactedResult,
        note: note.trim(),
        word: solution,
        imageSource: null,
        imageId: null,
        imageUrl,
      });
      setReportSuccess(true);
    } catch (error) {
      setReportError(
        error?.userMessage ||
          error?.message ||
          'Could not submit the report right now. Please try again.'
      );
    } finally {
      setReportSubmitting(false);
    }
  };

  return (
    <div className="modal-content game-result-modal game-result-modal--generations game-result-modal--scrollbar">
      <h2>{title}</h2>
      <RewardSummary
        rewardCoins={visibleRewardCoins}
        pointsAwarded={pointsAwarded}
        firstGameBonus={firstGameBonus}
      />
      <div className="modal-image-container game-result-modal__media">
        <ZoomedImagePreview
          imageUrl={imageUrl}
          alt={solution}
          zoomLevel={previewZoomLevel}
          zoomOrigin={previewZoomOrigin}
          size={240}
          borderRadius={10}
        />
        <p style={{ marginTop: '10px' }}>
          Word: <strong>{solution.toUpperCase()}</strong>
        </p>
        <AnsweredGridPreview guesses={guesses} solution={solution} />
        <DefinitionSection
          word={solution}
          showDefinition={showDefinition}
          definition={definition}
          isLoadingDefinition={isLoadingDefinition}
          toggleDefinition={toggleDefinition}
        />
        <ShareZoomedImageButton
          imageUrl={imageUrl}
          solution={solution}
          zoomLevel={copyZoomLevel}
          zoomOrigin={copyZoomOrigin}
          gameName="Zoomed In Generations"
          guesses={guesses}
          maxGuesses={5}
          disabled={!imageUrl}
        />
        {!isWin && (
          <p style={{ marginTop: '6px', color: '#cfcfcf' }}>
            Don't worry - AI images can be tricky. Try the next one.
          </p>
        )}
        <div className="game-report">
          <button
            type="button"
            className="game-report__toggle"
            onClick={onToggleReportForm}
            disabled={!canReport || reportSubmitting}
          >
            {showReportForm ? 'Hide report form' : reportSuccess ? 'Issue reported' : 'Report issue'}
          </button>

          {showReportForm && !reportSuccess && (
            <form className="game-report__form" onSubmit={onSubmitReport}>
              <p className="game-report__hint">
                Report mismatched image and word pairs, unclear images, or other round issues.
              </p>

              <fieldset className="game-report__reasons">
                <legend>Issue type</legend>
                {ISSUE_REPORT_REASONS.map((reason) => (
                  <label key={reason.value} className="game-report__reason-option">
                    <input
                      type="radio"
                      name="game-report-reason-generations"
                      value={reason.value}
                      checked={selectedReason === reason.value}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      disabled={reportSubmitting}
                    />
                    <span>{reason.label}</span>
                  </label>
                ))}
              </fieldset>

              <label className="game-report__impact">
                <input
                  type="checkbox"
                  checked={impactedResult}
                  onChange={(e) => setImpactedResult(e.target.checked)}
                  disabled={reportSubmitting}
                />
                <span>This affected my game result</span>
              </label>

              <label className="game-report__note-label" htmlFor="game-report-note-generations">
                Notes (optional)
              </label>
              <textarea
                id="game-report-note-generations"
                className="game-report__note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={ISSUE_REPORT_NOTE_MAX_LENGTH}
                rows={3}
                placeholder="Add details to help diagnose the issue."
                disabled={reportSubmitting}
              />
              <div className="game-report__note-meta">
                {remainingNoteChars} characters left
              </div>

              <button
                type="submit"
                className="game-report__submit"
                disabled={reportSubmitting}
              >
                {reportSubmitting ? 'Submitting...' : 'Submit report'}
              </button>

              {reportError && <p className="game-report__error">{reportError}</p>}
            </form>
          )}

          {showReportForm && reportSuccess && (
            <p className="game-report__success">
              Thanks. Your report was submitted for review.
            </p>
          )}
        </div>
      </div>
      {user ? (
        <div className="stats-container">
          <div className="stats-summary">
            <div className="stat-box">
              <span className="stat-number">{safeStats.currentStreak}</span>
              <span className="stat-label">Streak</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">{safeStats.maxStreak}</span>
              <span className="stat-label">Max Streak</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">{totalGames}</span>
              <span className="stat-label">Played</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">{winPercentage}</span>
              <span className="stat-label">Win %</span>
            </div>
          </div>
          <div className="guess-distribution">
            <h3>Guess Distribution</h3>
            {[1, 2, 3, 4, 5].map((guessNumber) => {
              const percentage = maxGuesses
                ? (safeStats.guesses[guessNumber] / maxGuesses) * 90
                : 0;
              return (
                <div key={guessNumber} className="guess-row">
                  <div className="guess-label">{guessNumber}</div>
                  <div className="guess-bar-container">
                    <div
                      className="guess-bar"
                      style={{
                        width: `${Math.min(90, percentage)}%`,
                      }}
                    >
                      {safeStats.guesses[guessNumber] || 0}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      <button
        className="modal-button"
        onClick={onNextRound}
        disabled={countdown > 0 || disableNext}
        style={{
          opacity: countdown > 0 || disableNext ? 0.5 : 1,
          cursor: countdown > 0 || disableNext ? 'not-allowed' : 'pointer',
        }}
      >
        {countdown > 0 ? `Next Round (${countdown})` : nextLabel}
      </button>
    </div>
  );
};

export default GenerationsGameModal;
